/**
 * What a snapshot carries, 20 times a second, and to what precision: the
 * schemas for `@massivewebgames/netsync`. Each client gets only what came
 * into its view (whole), the fields that changed in what it already had,
 * and what left. Within each entity the busiest fields come first, so the
 * change mask is usually one byte.
 *
 * Precision: positions to 1/128 of a block (finer than a pixel at any
 * zoom), velocities to 1/64, angles to 1/65536 of a turn. The server keeps
 * full precision; a client predicting itself is put back on the quantised
 * state each snapshot and the difference is far below what shows.
 */
import { SnapshotClient, SnapshotServer, entity, f, protocol, type AnyField, type Field, type Peer, type TypesOf } from "@massivewebgames/netsync";
import type { Gear, ItemId } from "./items";
import type { BandKind, BandState, ScriptKey, Track } from "./world/bands";
import type { School } from "./world/schools";
import { BODIES, CREATURE_KINDS, HAIRS, HATS, type CreatureState, type DropState, type Look, type PlayerState, type ProjectileState, type ShipState, type Tether, type TurretState } from "./world/types";

export { SnapshotClient, SnapshotServer, type Peer as SnapshotPeer };

/** Per-client facts that go with every snapshot. One per client (`id` is the client's seat), so `ack` can be its own. */
export interface SnapshotHeader {
  id: number;
  tick: number;
  /** Players in the area. */
  online: number;
  /** The clock drifting rocks follow their paths by (s), as of the end of this tick (see placeRocks). */
  time: number;
  /** The last input of yours the server has played (PlayerState.lastInputSeq, sent here rather than for every player). */
  ack: number;
}

/** A turret built on an asteroid: where it aims and whether it has reloaded. */
export interface AsteroidTurretState {
  /** `${rock}:${anchor}`. */
  id: string;
  rock: string;
  anchor: number;
  aim: number;
  cooldown: number;
}

/** A sun flaring or about to: 0..1 how far through the warning (1 = flaring). */
export interface FlareState {
  id: string;
  t: number;
}

/** The sphere shutting down (see shutdownAt): `id` is "sphere", `t` seconds since it began (see outageState). */
export interface OutageState {
  id: string;
  t: number;
}

/** A rift opening: where, and seconds open of NIGHT.rift.open. */
export interface RiftState {
  id: string;
  x: number;
  y: number;
  t: number;
}

/** An asteroid tile on fire. Membership is the whole message. */
export interface GroundFireState {
  /** `${rock}:${index}`. */
  id: string;
  rock: string;
  index: number;
}

/** A drifting rock knocked off its path (see Knock), by asteroid id; a stream rock's settled offset across the band too. */
export interface KnockState {
  id: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  o: number;
}

const look = f.obj<Look>({ hue: f.u16(), hat: f.enumOf(HATS), body: f.enumOf(BODIES), hair: f.enumOf(HAIRS) });

const turret = f.obj<TurretState>({ aim: f.angle(), cooldown: f.uquant(100), gunner: f.nullable(f.str()) });

const tether = f.obj<Tether>({
  anchor: f.uint(),
  point: f.vec2(128),
  rock: f.optional(f.str()),
  ship: f.optional(f.str()),
  creature: f.optional(f.str()),
  length: f.quant(100),
});

const school = f.obj<School>({
  round: f.quant(1000),
  turn: f.i8(),
  out: f.quant(100),
  n: f.u8(),
  alive: f.uint(),
  seed: f.uint(),
  scatter: f.quant(1000),
});

/**
 * A field whose stored form is worked out once per value (by identity): for
 * big values that are replaced, never changed in place, and compared every
 * tick (a band's script is rewritten a minute at a time, and sent only then).
 */
function once<V extends object, S>(inner: Field<V, S>): Field<V, S> {
  const memo = new WeakMap<V, S>();
  return {
    canon: (v) => {
      let s = memo.get(v);
      if (s === undefined) memo.set(v, (s = inner.canon(v)));
      return s;
    },
    equals: (a, b) => a === b || inner.equals(a, b),
    write: inner.write,
    read: inner.read,
  };
}

// Keys as quantiseKey makes them: 1/20 s, 1/32 block, 1/1000 radian.
const scriptKey = f.obj<ScriptKey>({ t: f.uquant(20), at: f.vec2(32), a: f.quant(1000), f: f.i8(), e: f.bool(), r: f.u8() });
const track = f.obj<Track>({ m: f.u8(), keys: f.array(scriptKey) });
const BAND_KINDS: readonly BandKind[] = ["grazer", "gasbag", "skyfish"];

export const SNAPSHOT = protocol({
  header: entity<SnapshotHeader>("id", { id: f.uint(), tick: f.uint(), online: f.uint(), time: f.f64(), ack: f.uint() }),
  players: entity<PlayerState>("id", {
    id: f.str(),
    // Finer than the rest: a player predicting themself starts each snapshot from this, and feet put a hair into the ground would be stepped up out of it.
    position: f.vec2(4096),
    velocity: f.vec2(64),
    aim: f.vec2(16),
    up: f.angle(),
    facing: f.i8(),
    grounded: f.bool(),
    using: f.bool(),
    health: f.uquant(10),
    sinceHurt: f.uquant(10),
    gear: f.optional(f.record("str", f.str<ItemId>()) as AnyField<Gear>),
    jetting: f.optional(f.bool()),
    frame: f.nullable(f.str()),
    braceHeld: f.bool(),
    pushHeld: f.optional(f.bool()),
    piloting: f.bool(),
    gunning: f.nullable(f.uint()),
    gunningAsteroid: f.optional(f.str()),
    holding: f.nullable(f.str<ItemId>()),
    grapple: f.maybe(f.vec2(128)),
    grappleShip: f.maybe(f.str()),
    grappleRock: f.maybe(f.str()),
    grappleOut: f.maybe(f.uquant(100)),
    grappleDir: f.optional(f.angle()),
    reelHeld: f.optional(f.bool()),
    vine: f.maybe(f.str()),
    vineT: f.optional(f.quant(1000)),
    vineWait: f.optional(f.uquant(100)),
    leap: f.optional(f.uquant(100)),
    gripWait: f.optional(f.uquant(100)),
    name: f.str(),
    look: f.optional(look),
    // Yours comes in the header; nobody needs anyone else's.
    lastInputSeq: f.omit(0),
    rode: f.omit(null),
  }),
  ships: entity<ShipState>("id", {
    id: f.str(),
    position: f.vec2(128),
    velocity: f.vec2(64),
    angle: f.angle(),
    angularVelocity: f.quant(1000),
    throttle: f.vec2(100),
    turrets: f.record("num", turret),
    fuel: f.uquant(100),
    water: f.uquant(100),
    gunCooldown: f.optional(f.uquant(100)),
    pilotId: f.nullable(f.str()),
    damage: f.optional(f.record("num", f.quant(100))),
    fires: f.optional(f.record("num", f.f64())),
    winches: f.optional(f.record("num", f.bool())),
    tether: f.maybe(tether),
    stalled: f.optional(f.uquant(100)),
    pirate: f.optional(f.bool()),
    pirateKind: f.optional(f.str()),
  }),
  projectiles: entity<ProjectileState>("id", {
    id: f.str(),
    position: f.vec2(128),
    velocity: f.vec2(64),
    ownerShipId: f.str(),
    // Counted down on the server only.
    life: f.omit(0),
  }),
  creatures: entity<CreatureState>("id", {
    id: f.str(),
    position: f.vec2(128),
    velocity: f.vec2(64),
    angle: f.angle(),
    local: f.optional(f.vec2(128)),
    health: f.uquant(10),
    angry: f.bool(),
    eating: f.optional(f.bool()),
    facing: f.optional(f.i8()),
    school: f.optional(school),
    stalled: f.optional(f.bool()),
    busy: f.optional(f.bool()),
    rock: f.optional(f.str()),
    kind: f.enumOf(CREATURE_KINDS),
  }),
  /** Calm bands of resident wildlife, with their scripts (see bands.ts); a disturbed band's members go as creatures. */
  bands: entity<BandState>("id", {
    id: f.str(),
    kind: f.enumOf(BAND_KINDS),
    seed: f.uint(),
    alive: f.uint(),
    size: f.u8(),
    turn: f.optional(f.i8()),
    out: f.optional(f.quant(10)),
    from: f.f64(),
    rocks: f.array(f.str()),
    tracks: once(f.array(track)),
  }),
  /** Loose items hanging in the world (see drops.ts); what each pile is doing is the server's own (f.omit). */
  drops: entity<DropState>("id", {
    id: f.str(),
    position: f.vec2(128),
    velocity: f.vec2(64),
    n: f.uint(),
    item: f.str<ItemId>(),
    frame: f.nullable(f.str()),
    wait: f.omit(0),
    life: f.omit(0),
    rode: f.omit(null),
    by: f.omit(null),
  }),
  turrets: entity<AsteroidTurretState>("id", { id: f.str(), aim: f.angle(), cooldown: f.uquant(100), rock: f.str(), anchor: f.uint() }),
  flares: entity<FlareState>("id", { id: f.str(), t: f.uquant(1000) }),
  outages: entity<OutageState>("id", { id: f.str(), t: f.uquant(10) }),
  rifts: entity<RiftState>("id", { id: f.str(), x: f.quant(100), y: f.quant(100), t: f.uquant(100) }),
  fires: entity<GroundFireState>("id", { id: f.str(), rock: f.str(), index: f.uint() }),
  knocks: entity<KnockState>("id", { id: f.str(), x: f.quant(1000), y: f.quant(1000), vx: f.quant(1000), vy: f.quant(1000), o: f.quant(100) }),
});

/** The snapshot's entity types, for `SnapshotClient<Snapshot>`. */
export type Snapshot = TypesOf<typeof SNAPSHOT>;
